/**
 * The public early-access page's words, in both languages, verbatim from the
 * approved reference (`Mouja Public Early Access v1.0`). English and Arabic
 * change together; `موجة` is the brand and is never transliterated.
 *
 * Nothing here promises an email: the confirmation says the request was
 * received and that Mouja will be in touch, which is what the service does.
 */

import type { Language } from '../../i18n/translations.js';

export interface PublicEarlyAccessCopy {
  readonly title: string;
  readonly skip: string;
  /** The language button names the *other* language, in that language. */
  readonly langLabel: string;
  readonly langLang: Language;
  readonly langAria: string;
  /** Announced to assistive technology once the switch has happened. */
  readonly langSwitched: string;
  readonly navCta: string;
  readonly eyebrow: string;
  readonly h1a: string;
  readonly h1b: string;
  readonly support: string;
  readonly cta: string;
  readonly ctaAria: string;
  readonly ctaNote: string;
  readonly reassure: string;
  readonly journey: readonly [string, string, string, string, string];
  readonly journeyAria: string;
  readonly arrow: string;
  readonly a11yFigure: string;
  readonly a11yVideo: string;
  readonly enlarge: string;
  readonly closeEnlarge: string;
  readonly unmute: string;
  readonly mute: string;
  readonly play: string;
  readonly pause: string;
  readonly lbPlay: string;
  readonly lbPause: string;
  readonly lbSoundOn: string;
  readonly lbSoundOff: string;
  readonly n1l: string;
  readonly n1v: string;
  readonly n2l: string;
  readonly n2v: string;
  readonly n3l: string;
  readonly n3v: string;
  readonly fTitle: string;
  readonly fMeta: string;
  readonly acEyebrow: string;
  readonly acTitle: string;
  readonly acDesc: string;
  readonly name: string;
  readonly email: string;
  readonly brand: string;
  readonly link: string;
  readonly optional: string;
  readonly submit: string;
  readonly submitting: string;
  readonly close: string;
  readonly privacy: string;
  readonly eName: string;
  readonly eEmail: string;
  readonly eBrand: string;
  readonly eLink: string;
  readonly summaryTitle: (count: number) => string;
  readonly fail: string;
  readonly doneTitle: string;
  readonly doneText: string;
  readonly doneLive: string;
}

export const PUBLIC_EARLY_ACCESS_COPY: Readonly<
  Record<Language, PublicEarlyAccessCopy>
> = {
  en: {
    title: 'Mouja — AI video for Saudi businesses and creators | Private beta',
    skip: 'Skip to content',
    langLabel: 'العربية',
    langLang: 'ar',
    langAria: 'Switch to Arabic',
    langSwitched: 'Language switched to English.',
    navCta: 'Join the waitlist',
    eyebrow: 'In development',
    h1a: 'Social videos.',
    h1b: 'Tailored to your business.',
    support: 'AI videos inspired by what works in your market.',
    cta: 'Try the demo',
    ctaAria: 'Try the prepared Mouja demo',
    ctaNote: 'Explore a prepared example.',
    reassure:
      'No content idea needed. Mouja handles the research and concepts. You approve before production.',
    journey: [
      'Understand what you offer',
      'Research your market',
      'Develop tailored concepts',
      'You approve',
      'Receive the finished video',
    ],
    journeyAria: 'How Mouja works, in five steps',
    arrow: '→',
    a11yFigure: 'Example of a finished 9:16 video',
    a11yVideo:
      'Example vertical video: a travel-style clip for a tourism or lifestyle account. Not generated for you.',
    enlarge: 'Enlarge example video',
    closeEnlarge: 'Close enlarged video',
    unmute: 'Turn sound on',
    mute: 'Turn sound off',
    play: 'Play example video',
    pause: 'Pause example video',
    lbPlay: 'Play',
    lbPause: 'Pause',
    lbSoundOn: 'Sound on',
    lbSoundOff: 'Sound off',
    n1l: 'Starts with',
    n1v: 'What you do',
    n2l: 'Made for',
    n2v: 'Reels · TikTok · Shorts',
    n3l: 'This example',
    n3v: 'A travel-style clip for a tourism or lifestyle account',
    fTitle: 'Example video',
    fMeta: '9:16 · Ready to post',
    acEyebrow: 'Private beta',
    acTitle: 'Request early access',
    acDesc:
      'Tell us a little about yourself and we’ll contact you as private-beta access becomes available.',
    name: 'Name',
    email: 'Email',
    brand: 'Creator or business name',
    link: 'Website or social profile',
    optional: 'Optional',
    submit: 'Request early access',
    submitting: 'Sending…',
    close: 'Close',
    privacy: 'We’ll only use this information to contact you about Mouja.',
    eName: 'Enter your name.',
    eEmail: 'Enter a valid email address.',
    eBrand: 'Enter your creator or business name.',
    eLink:
      'Enter one website, Instagram or TikTok profile, e.g. yourname.sa or @yourname.',
    summaryTitle: (count) =>
      count === 1
        ? '1 field needs attention'
        : `${String(count)} fields need attention`,
    fail: 'We couldn’t send your request. Please try again.',
    doneTitle: 'You’re on the early access list.',
    doneText: 'We’ll contact you as private-beta access becomes available.',
    doneLive: 'Request received. You’re on the early access list.',
  },
  ar: {
    title:
      'موجة — فيديو بالذكاء الاصطناعي للأنشطة وصنّاع المحتوى في السعودية | نسخة تجريبية خاصة',
    skip: 'تخطّي إلى المحتوى',
    langLabel: 'English',
    langLang: 'en',
    langAria: 'Switch to English',
    langSwitched: 'تم التبديل إلى العربية.',
    navCta: 'انضم للقائمة',
    eyebrow: 'قيد التطوير',
    h1a: 'فيديوهات سوشال.',
    h1b: 'تناسب نشاطك.',
    support: 'بالذكاء الاصطناعي، ومستوحاة من المحتوى الناجح في مجالك.',
    cta: 'جرّب موجة',
    ctaAria: 'جرّب مثال موجة الجاهز',
    ctaNote: 'جرّب مثالًا جاهزًا.',
    reassure: 'ما تحتاج فكرة جاهزة. موجة تتولى البحث والأفكار، وأنت تعتمد قبل الإنتاج.',
    journey: [
      'فهم ما تقدّمه',
      'دراسة سوقك',
      'تطوير أفكار مخصّصة',
      'اعتمادك',
      'استلام الفيديو',
    ],
    journeyAria: 'كيف تعمل موجة، في خمس خطوات',
    arrow: '←',
    a11yFigure: 'مثال لفيديو 9:16 منتهٍ',
    a11yVideo:
      'مثال لفيديو عمودي: مقطع بأسلوب السفر لحساب سياحي أو لايف ستايل. ليس مُنتَجًا لك.',
    enlarge: 'تكبير الفيديو النموذجي',
    closeEnlarge: 'إغلاق العرض المكبّر',
    unmute: 'تشغيل الصوت',
    mute: 'إيقاف الصوت',
    play: 'تشغيل الفيديو النموذجي',
    pause: 'إيقاف الفيديو النموذجي مؤقتًا',
    lbPlay: 'تشغيل',
    lbPause: 'إيقاف مؤقت',
    lbSoundOn: 'تشغيل الصوت',
    lbSoundOff: 'إيقاف الصوت',
    n1l: 'يبدأ من',
    n1v: 'ما تقدّمه',
    n2l: 'مصمم لـ',
    n2v: 'Reels · TikTok · Shorts',
    n3l: 'هذا المثال',
    n3v: 'مقطع بأسلوب السفر لحساب سياحي أو لايف ستايل',
    fTitle: 'فيديو نموذجي',
    fMeta: '9:16 · جاهز للنشر',
    acEyebrow: 'نسخة تجريبية خاصة',
    acTitle: 'طلب تجربة المنصة',
    acDesc: 'أخبرنا قليلًا عنك وبنتواصل معك عند توفر الوصول للنسخة التجريبية الخاصة.',
    name: 'الاسم',
    email: 'البريد الإلكتروني',
    brand: 'اسم الحساب أو النشاط',
    link: 'الموقع الإلكتروني أو حساب التواصل',
    optional: 'اختياري',
    submit: 'طلب تجربة المنصة',
    submitting: 'جارٍ الإرسال…',
    close: 'إغلاق',
    privacy: 'سنستخدم هذه المعلومات فقط للتواصل معك بخصوص موجة.',
    eName: 'أدخل اسمك.',
    eEmail: 'أدخل بريدًا إلكترونيًا صحيحًا.',
    eBrand: 'أدخل اسم الحساب أو النشاط.',
    eLink:
      'أدخل موقعًا أو حسابًا واحدًا على Instagram أو TikTok، مثل yourname.sa أو ‎@yourname.',
    summaryTitle: (count) =>
      count === 1
        ? 'حقل واحد يحتاج إلى مراجعة'
        : `${String(count)} حقول تحتاج إلى مراجعة`,
    fail: 'لم نتمكن من إرسال طلبك. حاول مرة أخرى.',
    doneTitle: 'تم تسجيل اهتمامك.',
    doneText: 'بنتواصل معك عند توفر الوصول للنسخة التجريبية الخاصة.',
    doneLive: 'تم استلام طلبك. تم تسجيل اهتمامك.',
  },
};

/** The enlarged view's caption: the example note and the aspect ratio. */
export function lightboxNote(copy: PublicEarlyAccessCopy): string {
  return `${copy.n3v} · 9:16`;
}
